import * as React from "react";
import { DEFAULT_EXPAND_RATIO } from "./button-distribute-tokens";

import type { DistributeMode } from "./types";

interface UseDistributeWeightsOptions {
	count: number;
	weights?: number[];
	activeIndex: number | null;
	expandRatio?: number;
	mode?: DistributeMode;
	isTextItem?: boolean[];
}

/**
 * Calculates normalized flexGrow weights for ButtonDistribute items.
 *
 * Preserves the total sum of weights when an item is expanded so that
 * the total container width stays strictly at 100%.
 * Non-active items with text labels are protected with a higher min-weight
 * floor to prevent label truncation.
 */
export function useDistributeWeights({
	count,
	weights,
	activeIndex,
	expandRatio = DEFAULT_EXPAND_RATIO,
	mode = "dynamic",
	isTextItem,
}: UseDistributeWeightsOptions): number[] {
	return React.useMemo(() => {
		if (count <= 0) return [];

		if (mode === "mixed") {
			const baseWeights = new Array(count).fill(0).map((_, idx) => {
				if (idx === 0 || idx === count - 1) return 0;
				return weights && weights[idx] !== undefined ? weights[idx] : 1;
			});

			if (
				activeIndex === null ||
				activeIndex < 0 ||
				activeIndex >= count ||
				count === 1
			) {
				return baseWeights;
			}

			const result = [...baseWeights];
			const expandAmount = expandRatio * 1.5;

			if (activeIndex === 0) {
				// Leading item active
				result[0] = expandAmount;
				for (let i = 1; i < count - 1; i++) {
					result[i] = Math.max(0.5, baseWeights[i] - expandRatio * 0.4);
				}
			} else if (activeIndex === count - 1) {
				// Trailing item active
				result[count - 1] = expandAmount;
				for (let i = 1; i < count - 1; i++) {
					result[i] = Math.max(0.5, baseWeights[i] - expandRatio * 0.4);
				}
			} else {
				// Center item active
				result[activeIndex] = baseWeights[activeIndex] + expandRatio;
				for (let i = 1; i < count - 1; i++) {
					if (i !== activeIndex) {
						result[i] = Math.max(0.5, baseWeights[i] - expandRatio * 0.5);
					}
				}
			}

			return result;
		}

		// Determine base weights for dynamic and fixed modes
		let baseWeights: number[];
		if (weights && weights.length === count) {
			baseWeights = [...weights];
		} else if (mode === "dynamic" && count === 3) {
			// Spec default: 1 : 2 : 1 for Prev : Play : Next in dynamic mode
			baseWeights = [1, 2, 1];
		} else {
			// Fixed mode or equal weights default: 1 : 1 : ... : 1
			baseWeights = new Array(count).fill(1);
		}

		if (
			activeIndex === null ||
			activeIndex < 0 ||
			activeIndex >= count ||
			count === 1
		) {
			return baseWeights;
		}

		const totalWeight = baseWeights.reduce((acc, w) => acc + w, 0);
		const activeBaseWeight = baseWeights[activeIndex];
		const expandAmount = expandRatio * totalWeight;

		// Minimum weight floor per item to prevent content truncation
		const minWeights = baseWeights.map((w, idx) => {
			if (isTextItem?.[idx]) {
				// Text items need higher floor so label isn't clipped
				return Math.max(0.75, w * 0.65);
			}
			return 0.4; // Icon buttons can shrink to compact circle
		});

		// Available shrink capacity for non-active items
		const shrinkCapacity = baseWeights.map((w, idx) =>
			idx === activeIndex ? 0 : Math.max(0, w - minWeights[idx]),
		);
		const totalCapacity = shrinkCapacity.reduce((acc, cap) => acc + cap, 0);

		const result = [...baseWeights];

		if (totalCapacity > 0) {
			const actualExpand = Math.min(expandAmount, totalCapacity);
			result[activeIndex] = activeBaseWeight + actualExpand;

			for (let i = 0; i < count; i++) {
				if (i !== activeIndex) {
					const shrinkAmount =
						actualExpand * (shrinkCapacity[i] / totalCapacity);
					result[i] = Math.max(minWeights[i], baseWeights[i] - shrinkAmount);
				}
			}
		}

		return result;
	}, [count, weights, activeIndex, expandRatio, mode, isTextItem]);
}
